/**
 * **Trainings** (`/trainings`, 2026-09-17) — the PDFs and videos XOP Networks
 * publishes to train a reseller on a release.
 *
 * Staff add a training to a release VERSION in the command center (CRM ▸
 * Releases ▸ version ▸ Trainings), exactly the way they add release
 * documentation — but a training is never listed on the Releases page or in
 * Documentation. It lives here, on its own page (CEO, 2026-09-17).
 *
 * The page is a search box and the trainings grouped by RELEASE (newest
 * first) then VERSION. A row opens the ordinary article reader at
 * `/trainings/:slug` (PDF inline through pdf.js, video in the player), whose
 * Back returns here. WHICH trainings appear is portal-api's decision
 * (`Documents::AccessResolver`); this page groups and filters what arrives.
 *
 * Above them sit the COURSES (2026-11-02): one card per training module — a
 * service or an extension taught as short videos — opening the module
 * player (`ModulePlayerPage`). The two lists load independently: a module
 * list that fails to load hides its section rather than the whole page.
 */
import { Box, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { FileText, GraduationCap, PlayCircle } from "lucide-react";
import { useMemo } from "react";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { PageHeader } from "../../../components/ui/page-header.js";
import { PageLoader } from "../../../components/ui/page-loader.js";
import { isPermissionDenied } from "../../../lib/api.js";
import { DocSearchBox } from "../../knowledge-base/DocSearchBox.js";
import type { DocSummary } from "../../knowledge-base/types.js";
import { listTrainings } from "./api.js";
import { ModuleCards } from "./ModuleCards.js";
import { listModules } from "./modules-api.js";
import { groupTrainings, searchTrainings, trainingKind } from "./training-groups.js";
import { trainingPath } from "./training-paths.js";

export const TRAININGS_TITLE = "Online Training";

export function TrainingsPage(): JSX.Element {
  const [params] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const query = useQuery({ queryKey: ["reseller", "trainings"], queryFn: listTrainings });
  const modulesQuery = useQuery({ queryKey: ["reseller", "training-modules"], queryFn: listModules });
  const modules = modulesQuery.data ?? [];

  const all = query.data ?? [];
  const groups = useMemo(() => groupTrainings(searchTrainings(query.data ?? [], q)), [query.data, q]);

  if (query.isLoading) return <PageLoader />;

  if (query.error) {
    if (isPermissionDenied(query.error)) {
      return <NoAccessPanel permission={query.error.permission ?? "documentation:view"} />;
    }
    return (
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <PageHeader title={TRAININGS_TITLE} />
        <Notice testId="trainings-error">
          We could not load the trainings:{" "}
          {query.error instanceof Error ? query.error.message : "unknown error"}. Try again in a moment.
        </Notice>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="trainings-page">
      <PageHeader
        title={TRAININGS_TITLE}
        description="Video courses for every XOP service and extension, and the training XOP publishes for each release."
      />

      {modules.length > 0 && <ModuleCards modules={modules} />}

      {modules.length > 0 && all.length > 0 && (
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, mt: 1 }}>
          Release trainings
        </Typography>
      )}

      {all.length === 0 && modules.length > 0 ? null : all.length === 0 ? (
        <Notice testId="trainings-empty">
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>No trainings yet</Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
            When XOP Networks publishes a training for a release, it appears here.
          </Typography>
        </Notice>
      ) : (
        <>
          <DocSearchBox placeholder="Search trainings…" ariaLabel="Search trainings" />
          {groups.length === 0 ? (
            <Notice testId="trainings-search-empty">
              No trainings match <strong>“{q}”</strong>. Try fewer or different words.
            </Notice>
          ) : (
            groups.map((group) => (
              <Box
                component="section"
                key={group.release}
                aria-label={releaseHeading(group.release)}
                data-testid={`training-release-${group.release}`}
                sx={{ display: "grid", gap: 1.5 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
                    {releaseHeading(group.release)}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${group.count} training${group.count === 1 ? "" : "s"}`}
                    sx={{ height: 20 }}
                  />
                </Box>
                {group.versions.map((v) => (
                  <Box
                    key={v.version ?? "all"}
                    data-testid={`training-version-${v.version ?? `${group.release}-all`}`}
                    sx={{ display: "grid", gap: 1 }}
                  >
                    <Typography
                      component="h4"
                      sx={{
                        fontSize: 11,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "text.disabled",
                      }}
                    >
                      {v.version ? `Version ${v.version}` : `All versions of ${group.release}`}
                    </Typography>
                    {v.trainings.map((t) => (
                      <TrainingRow key={t.id} training={t} />
                    ))}
                  </Box>
                ))}
              </Box>
            ))
          )}
        </>
      )}
    </Box>
  );
}

function releaseHeading(release: string): string {
  return release === "Other" ? "Other trainings" : `Release ${release}`;
}

function TrainingRow({ training }: { training: DocSummary }): JSX.Element {
  const kind = trainingKind(training);
  const Icon = kind === "Video" ? PlayCircle : kind === "PDF" ? FileText : GraduationCap;
  return (
    <Box
      component={RouterLink}
      to={trainingPath(training.slug)}
      data-testid={`training-row-${training.id}`}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 1.5,
        py: 1.25,
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        color: "text.primary",
        textDecoration: "none",
        transition: (t) => t.transitions.create(["background-color", "border-color"], { duration: 150 }),
        "&:hover": { bgcolor: "action.hover", borderColor: "primary.main" },
      }}
    >
      <Box aria-hidden sx={{ display: "inline-flex", color: "primary.main", flexShrink: 0, mt: 0.25 }}>
        <Icon size={18} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{training.title}</Typography>
          <Chip label={kind} size="small" variant="outlined" sx={{ height: 18 }} />
        </Box>
        {training.summary && (
          <Typography
            sx={{
              mt: 0.25,
              fontSize: 12.5,
              color: "text.secondary",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {training.summary}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function Notice({ testId, children }: { testId: string; children: ReactNode }): JSX.Element {
  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
        p: 4,
        textAlign: "center",
        fontSize: 14,
        color: "text.secondary",
      }}
    >
      {children}
    </Box>
  );
}

export default TrainingsPage;
